/**
 * The first-run tutorial, step by step.
 *
 * KEEP THIS IN SYNC WITH THE APP. Every change a coach can see — a new tool,
 * a removed control, a renamed one, a new keyboard key — updates this file in
 * the same pull request: add a step for something new (with the version it
 * ships in), delete the step of something removed, reword what changed.
 * `pnpm verify` walks every step and fails when a step's target is no longer
 * on screen, but it cannot notice a feature that has no step — that part is on
 * whoever adds the feature. (See CLAUDE.md, "Tutorial".)
 *
 * Each step points at one element through its `data-tutorial` attribute — a
 * name chosen for the tutorial, never a CSS class or visible text, so a
 * restyle can't silently break a step. A coach sees every step they have not
 * confirmed, in this order; a release that adds steps therefore shows
 * returning coaches only the new ones.
 */

import type { SheetTab } from '../layout.ts';

export interface TutorialStep {
  /** Stable, never reused: it is stored with each coach's progress. */
  readonly id: string;
  /** The `data-tutorial` value of the element to highlight. */
  readonly target: string;
  readonly title: string;
  readonly text: string;
  /**
   * The same, said to a phone: taps and the on-screen buttons instead of
   * clicks and keys. Absent when `text` already reads right on a phone.
   */
  readonly phoneText?: string;
  /**
   * On a phone, the sheet tab this step's element lives in. The tutorial opens
   * it before highlighting the element, and closes it again afterwards.
   */
  readonly sheet?: SheetTab;
  /** The release this step first shipped in. Documentation only. */
  readonly introducedIn: string;
}

export const TUTORIAL_STEPS: readonly TutorialStep[] = [
  {
    id: 'board',
    target: 'board',
    title: 'Your board',
    text: 'This is the pool. Your players, the opposition and the ball are already set up. Everything you draw here is saved with your account, so it is waiting for you on any device.',
    introducedIn: '0.7.0',
  },
  {
    id: 'select',
    target: 'tool-select',
    title: 'Select & move',
    text: 'Drag any player or the ball to move them. Drag a box over open water to move several at once. Double-click a cap to change the number. (V)',
    phoneText: 'Drag any player or the ball to move them. Drag a box over open water to move several at once. Double-tap a cap to change the number.',
    introducedIn: '0.7.0',
  },
  {
    id: 'swim',
    target: 'tool-move',
    title: 'Swim',
    text: 'Draw from a player to where they swim. They follow your pen as you draw. A player with the ball takes it along: that is a dribble. The ball always goes with the nearest player, so to show a steal, drag the ball onto the defender. (A)',
    phoneText: 'Draw from a player to where they swim. They follow your finger as you draw. A player with the ball takes it along: that is a dribble. The ball always goes with the nearest player, so to show a steal, drag the ball onto the defender.',
    introducedIn: '0.7.0',
  },
  {
    id: 'pass-shot',
    target: 'tool-pass',
    title: 'Pass and shot',
    text: 'Draw from the player with the ball to where it goes. The ball follows your pen and the player stays put. A pass waits for its player to reach the ball. (P for pass, S for shot)',
    phoneText: 'Draw from the player with the ball to where it goes. The ball follows your finger and the player stays put. A pass waits for its player to reach the ball.',
    introducedIn: '0.7.0',
  },
  {
    id: 'phases',
    target: 'phases',
    title: 'Phases',
    text: 'A play is a list of phases. + Phase adds one after the one you are on; then move the players and draw their swims and passes. Each phase shows only its own drawings. Click a phase to jump to it. (K adds a phase)',
    phoneText: 'A play is a list of phases. + Phase adds one after the one you are on; then move the players and draw their swims and passes. Each phase shows only its own drawings. Tap a phase to jump to it.',
    introducedIn: '0.7.0',
  },
  {
    id: 'stepping',
    target: 'stepping',
    title: 'Walk through the play',
    text: 'Tap Space to play the next phase, Shift+Space to go back. Quick taps shuffle through without waiting. Hold the key, or the button, to play on smoothly; let go and it stops on the next phase.',
    phoneText: 'Tap Next ▸ to play the next phase and ◂ to go back. Quick taps shuffle through without waiting. Hold a button to play on smoothly; let go and it stops on the next phase.',
    introducedIn: '0.7.0',
  },
  {
    // Phone only: the tabs are not on a desktop, so the step is never shown there.
    id: 'sheet',
    target: 'sheet-tabs',
    title: 'Everything else',
    text: 'Ink, Setup, Record and More open from these tabs. Tap the tab again, tap the board or swipe the sheet down to close it. Tip: add the board to your home screen from your browser’s menu and it opens full screen, like an app. You sign in once more there.',
    introducedIn: '0.8.0',
  },
  {
    id: 'roster',
    target: 'roster',
    title: 'The teams',
    text: 'Take players off with − and put them back with +. + always brings back the standard seven: Goalie, Center, Center defender, two Wings and two Flats. Turn on position labels and double-click one to rename it.',
    phoneText: 'Take players off with − and put them back with +. + always brings back the standard seven: Goalie, Center, Center defender, two Wings and two Flats. Turn on position labels and double-tap one to rename it.',
    sheet: 'setup',
    introducedIn: '0.7.0',
  },
  {
    id: 'formations',
    target: 'formations',
    title: 'Formations',
    text: 'Reset the board to a built-in formation, or save your own setup — numbers and position names included — and bring it back any time. The players glide into place.',
    sheet: 'setup',
    introducedIn: '0.7.0',
  },
  {
    id: 'views',
    target: 'views',
    title: 'Half-court views',
    text: 'Show the whole pool, or zoom to one half around its goal. The full width always stays on screen. (H cycles through them)',
    phoneText: 'Show the whole pool, or zoom to one half around its goal. The full width always stays on screen.',
    introducedIn: '0.7.0',
  },
  {
    id: 'recording',
    target: 'recording',
    title: 'Record and share',
    text: 'Start a recording, walk through the play with Space and talk over it — your pointer is in the video too. When you stop, the video takes a moment to prepare; then share it with your team. It plays on any phone or computer.',
    phoneText: 'Start a recording, walk through the play with Next ▸ and talk over it. It keeps recording while this sheet is closed. When you stop, the video takes a moment to prepare; then share it with your team. It plays on any phone or computer.',
    sheet: 'record',
    introducedIn: '0.7.0',
  },
  {
    id: 'saved-plays',
    target: 'saved-plays',
    title: 'Saved plays',
    text: 'Happy with a play? Save it under a name and it is kept with your account. Open it any time to walk through it or record it again; Undo brings back what was on the board. New play keeps your teams and starts the phases over.',
    sheet: 'record',
    introducedIn: '0.9.0',
  },
];

/**
 * The steps to show: on a replay every step, otherwise every step the coach
 * has not confirmed, in tutorial order.
 */
export function stepsToShow(
  steps: readonly TutorialStep[],
  confirmed: ReadonlySet<string>,
  replay = false,
): readonly TutorialStep[] {
  return replay ? steps : steps.filter((step) => !confirmed.has(step.id));
}

/** What a step says on this screen: the phone wording on a phone, when it has one. */
export function textFor(step: TutorialStep, phone: boolean): string {
  return phone ? (step.phoneText ?? step.text) : step.text;
}
